/* eslint-disable no-console */
import React, { useState, useEffect, ChangeEvent } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  Alert,
  Stack,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ArrowBack,
  Schedule,
  Assignment,
  LocationOn,
  Person,
  CalendarToday,
  AccessTime,
  Close,
  WarningAmber,
  CheckCircle,
  Cancel,
  Save,
  AttachFile,
  PhotoCamera,
  InsertDriveFile,
  ExpandMore,
  ChecklistRtl,
  CameraAlt
} from '@mui/icons-material';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Inspection, Attachment } from './types';
import SimpleCamera from './CameraCapture';
import CompletionDialog from './CompletionDialog';
import { ChecklistItem } from './types'

interface InProgressProps {
  inspection: Inspection | null;
  onBack: () => void;
  onComplete: (
    completionType: 'pass' | 'fail' | 'not-completed',
    completionNotes: string,
    attachments: Attachment[],
    checklist: ChecklistItem[]
  ) => Promise<void>;
}

/**
 * InProgress - Full page component for conducting an inspection
 */
const InProgress: React.FC<InProgressProps> = ({
  inspection,
  onBack,
  onComplete
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [showBackConfirmation, setShowBackConfirmation] = useState(false);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);

  // File upload and photo capture states
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [cameraOpen, setCameraOpen] = useState<boolean>(false);
  const [isCameraSupported, setIsCameraSupported] = useState<boolean>(false);

  // Accordion expansion states
  const [checklistExpanded, setChecklistExpanded] = useState<boolean>(true); // Start with checklist open
  const [attachmentsExpanded, setAttachmentsExpanded] = useState<boolean>(false);

  // Completion dialog states
  const [completionDialogOpen, setCompletionDialogOpen] = useState<boolean>(false);
  const [completionType, setCompletionType] = useState<'pass' | 'fail' | 'not-completed' | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Initialize checklist when inspection data is available
  React.useEffect(() => {
    if (inspection?.inspectionCheckList) {
      const initialChecklist = inspection.inspectionCheckList.map(item => ({
        Name: item.Name,
        Checked: false
      }));
      setChecklist(initialChecklist);
    }
  }, [inspection]);

  // Check camera support on component mount
  useEffect(() => {
    const checkCameraSupport = () => {
      const isSupported = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
      setIsCameraSupported(isSupported);
      console.log('Camera support detected:', isSupported);
    };

    checkCameraSupport();
  }, []);

  const handleBackClick = () => {
    setShowBackConfirmation(true);
  };

  const handleConfirmBack = () => {
    setShowBackConfirmation(false);
    onBack();
  };

  const handleCancelBack = () => {
    setShowBackConfirmation(false);
  };

  // Handle checklist item toggle
  const handleChecklistToggle = (index: number) => {
    setChecklist(prev => prev.map((item, i) =>
      i === index ? { ...item, Checked: !item.Checked } : item
    ));
  };

  // Handle accordion expansion
  const handleChecklistAccordionChange = () => {
    setChecklistExpanded(!checklistExpanded);
    if (!checklistExpanded) {
      setAttachmentsExpanded(false); // Close attachments when opening checklist
    }
  };

  const handleAttachmentsAccordionChange = () => {
    setAttachmentsExpanded(!attachmentsExpanded);
    if (!attachmentsExpanded) {
      setChecklistExpanded(false); // Close checklist when opening attachments
    }
  };

  // Handle file upload
  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;

    const files = Array.from(event.target.files);

    // Create new attachments from selected files
    const newAttachments: Attachment[] = files.map(file => ({
      id: `temp-${Date.now()}-${file.name}`,
      name: file.name,
      type: file.type,
      size: file.size,
      file // Keep the actual file for upload
    }));

    setAttachments(prev => [...prev, ...newAttachments]);
    toast.success(`${files.length} file(s) added successfully`);
  };

  // Handle removing an attachment
  const handleRemoveAttachment = (attachmentId: string) => {
    setAttachments(prev => prev.filter(att => att.id !== attachmentId));
    toast.success('File removed');
  };

  // Open camera
  const handleOpenCamera = () => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ video: true })
        .then(stream => {
          // Stop the stream immediately, we just needed the permission
          stream.getTracks().forEach(track => track.stop());
          setCameraOpen(true);
          console.log('Camera permission granted, opening camera dialog');
        })
        .catch(err => {
          console.error('Camera permission error:', err);
          if (err.name === 'NotAllowedError') {
            toast.error('Camera access was denied. Please allow camera access in your browser settings.');
          } else if (err.name === 'NotFoundError') {
            toast.error('No camera found on this device.');
          } else {
            toast.error('Could not access camera: ' + err.message);
          }
        });
    } else {
      toast.error('Your browser does not support camera access.');
    }
  };

  // Close camera
  const handleCloseCamera = () => {
    setCameraOpen(false);
  };

  // Handle captured photo
  const handleCapturedPhoto = (imageBlob: Blob, fileName: string) => {
    const newAttachment: Attachment = {
      id: `photo-${Date.now()}`,
      name: fileName,
      type: 'image/jpeg',
      size: imageBlob.size,
      file: imageBlob as File
    };

    setAttachments(prev => [...prev, newAttachment]);
    toast.success('Photo captured and added successfully');
  };

  // Handle completion button clicks
  const handleMarkAsPass = () => {
    setCompletionType('pass');
    setCompletionDialogOpen(true);
  };

  const handleMarkAsFail = () => {
    setCompletionType('fail');
    setCompletionDialogOpen(true);
  };

  const handleMarkAsNotCompleted = () => {
    setCompletionType('not-completed');
    setCompletionDialogOpen(true);
  };

  // Handle completion dialog close
  const handleCloseCompletionDialog = () => {
    setCompletionDialogOpen(false);
    setCompletionType(null);
  };

  // Handle completion submission
  const handleCompleteInspection = async (completionNotes: string, completionAttachments: Attachment[]) => {
    if (!completionType) return;

    setLoading(true);
    try {
      // Combine attachments from InProgress with any additional ones from completion dialog
      const allAttachments = [...attachments, ...completionAttachments];

      // Pass all data to parent component including completion type
      await onComplete(completionType, completionNotes, allAttachments, checklist);

      // Close dialog
      handleCloseCompletionDialog();

      toast.success('Inspection completed successfully');
    } catch (error) {
      console.error('Error completing inspection:', error);
      toast.error('Failed to complete inspection');
    } finally {
      setLoading(false);
    }
  };

  // Calculate completion percentage
  const getCompletionPercentage = () => {
    if (checklist.length === 0) return 0;
    const completedItems = checklist.filter(item => item.Checked).length;
    return Math.round((completedItems / checklist.length) * 100);
  };

  const completionPercentage = getCompletionPercentage();

  if (!inspection) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          No inspection data available
        </Typography>
      </Container>
    );
  }

  return (
    <>
      <Container
        maxWidth="lg"
        sx={{
          mt: { xs: 2, md: 3 },
          mb: { xs: 4, md: 6 },
          px: { xs: 2, sm: 3, md: 3 }
        }}
      >
        {/* Header */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: { xs: 3, md: 4 },
          gap: { xs: 1, sm: 2 }
        }}>
          <IconButton
            onClick={handleBackClick}
            sx={{
              bgcolor: 'action.hover',
              '&:hover': { bgcolor: 'action.selected' },
              mr: { xs: 1, sm: 2 }
            }}
          >
            <ArrowBack />
          </IconButton>

          <Box sx={{ flex: 1 }}>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              fontWeight="bold"
              sx={{
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                mb: 0.5
              }}
            >
              Inspection In Progress
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{
                fontSize: { xs: '0.875rem', sm: '1rem' },
                textAlign: 'center'
              }}
            >
              Complete the inspection checklist below
            </Typography>
          </Box>

          <Chip
            label="In Progress"
            color="warning"
            icon={<Schedule />}
            sx={{
              height: { xs: 32, sm: 36 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              fontWeight: 600
            }}
          />
        </Box>

        {/* Inspection Overview Card */}
        <Card sx={{ mb: { xs: 3, md: 4 }, boxShadow: 1, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontSize: { xs: '1.125rem', sm: '1.25rem' },
                fontWeight: 600,
                mb: 2
              }}
            >
              {inspection.applicationTitle}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: { xs: '0.875rem', sm: '0.925rem' },
                mb: 3
              }}
            >
              Application #: {inspection.applicationNumber}
            </Typography>

            {/* Info Grid */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
              gap: { xs: 2, sm: 3 },
              '& > div': {
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5
              }
            }}>
              <Box>
                <CalendarToday
                  sx={{
                    color: 'primary.main',
                    fontSize: '1.25rem',
                    mt: 0.25
                  }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.75rem', fontWeight: 500 }}
                  >
                    Inspection Date
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: { xs: '0.875rem', sm: '0.925rem' } }}
                  >
                    {format(new Date(inspection.inspectionDate), 'MMM dd, yyyy')}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <AccessTime
                  sx={{
                    color: 'primary.main',
                    fontSize: '1.25rem',
                    mt: 0.25
                  }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.75rem', fontWeight: 500 }}
                  >
                    Time Slot
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: { xs: '0.875rem', sm: '0.925rem' } }}
                  >
                    {inspection.timeSlot}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Assignment
                  sx={{
                    color: 'primary.main',
                    fontSize: '1.25rem',
                    mt: 0.25
                  }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.75rem', fontWeight: 500 }}
                  >
                    Inspection Type
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: { xs: '0.875rem', sm: '0.925rem' } }}
                  >
                    {inspection.inspectionType}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1', md: '1' } }}>
                <LocationOn
                  sx={{
                    color: 'primary.main',
                    fontSize: '1.25rem',
                    mt: 0.25
                  }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.75rem', fontWeight: 500 }}
                  >
                    Property Address
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '0.925rem' },
                      wordBreak: 'break-word'
                    }}
                  >
                    {inspection.propertyAddress}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1', md: '2 / -1' } }}>
                <Person
                  sx={{
                    color: 'primary.main',
                    fontSize: '1.25rem',
                    mt: 0.25
                  }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.75rem', fontWeight: 500 }}
                  >
                    Contact Information
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: { xs: '0.875rem', sm: '0.925rem' } }}
                  >
                    {inspection.contactName} • {inspection.contactPhone}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Attachments Accordion */}
        <Accordion
          expanded={attachmentsExpanded}
          onChange={handleAttachmentsAccordionChange}
          sx={{
            mb: { xs: 4, md: 5 },
            boxShadow: 1,
            border: '1px solid',
            borderColor: 'divider',
            '&:before': {
              display: 'none',
            },
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              bgcolor: 'grey.50',
              color: 'text.primary',
              borderBottom: attachmentsExpanded ? '1px solid' : 'none',
              borderColor: 'divider',
              '& .MuiAccordionSummary-content': {
                alignItems: 'center',
                gap: 2
              },
              '&:hover': {
                bgcolor: 'grey.100'
              },
              py: { xs: 1, sm: 1.5 }
            }}
          >
            <CameraAlt sx={{
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              color: 'secondary.main'
            }} />
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  fontWeight: 600,
                  color: 'text.primary'
                }}
              >
                Documentation & Photos
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  color: 'text.secondary',
                  mt: 0.25
                }}
              >
                Upload files and capture photos
              </Typography>
            </Box>
            <Chip
              label={attachments.length}
              size="small"
              color="secondary"
              variant="outlined"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '0.7rem', sm: '0.75rem' }
              }}
            />
          </AccordionSummary>

          <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 3, sm: 3 } }}>
            {/* Upload Buttons */}
            <Grid container spacing={{ xs: 2, sm: 2 }} sx={{ mb: { xs: 3, sm: 3 } }}>
              <Grid item xs={12} sm={6}>
                <input
                  accept="image/*,application/pdf,.doc,.docx"
                  id="upload-inspection-file"
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
                <label htmlFor="upload-inspection-file" style={{ width: '100%', display: 'block' }}>
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<AttachFile />}
                    fullWidth
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '0.925rem' },
                      py: { xs: 1.5, sm: 1.75 },
                      fontWeight: 500,
                      height: { xs: 52, sm: 56 },
                      borderColor: 'divider',
                      color: 'text.primary',
                      bgcolor: 'background.paper',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    Upload Files
                  </Button>
                </label>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Button
                  variant="outlined"
                  startIcon={<PhotoCamera />}
                  onClick={handleOpenCamera}
                  disabled={!isCameraSupported}
                  title={!isCameraSupported ? 'Camera not supported on this device or browser' : 'Take a photo with your camera'}
                  fullWidth
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '0.925rem' },
                    py: { xs: 1.5, sm: 1.75 },
                    fontWeight: 500,
                    height: { xs: 52, sm: 56 },
                    borderColor: isCameraSupported ? 'divider' : 'action.disabled',
                    color: isCameraSupported ? 'text.primary' : 'text.disabled',
                    bgcolor: 'background.paper',
                    '&:hover': {
                      borderColor: isCameraSupported ? 'secondary.main' : 'action.disabled',
                      bgcolor: isCameraSupported ? 'action.hover' : 'transparent'
                    }
                  }}
                >
                  Take Photo
                </Button>
              </Grid>
            </Grid>

            {/* Camera Support Warning */}
            {!isCameraSupported && (
              <Alert
                severity="warning"
                sx={{
                  mb: { xs: 3, sm: 3 },
                  '& .MuiAlert-message': {
                    fontSize: { xs: '0.875rem', sm: '0.925rem' }
                  }
                }}
              >
                Camera functionality is not available on this device or browser. You can still upload files from your device.
              </Alert>
            )}

            {/* Attachments List */}
            {attachments.length > 0 ? (
              <Box>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{
                    fontSize: { xs: '0.925rem', sm: '1rem' },
                    fontWeight: 600,
                    mb: { xs: 2, sm: 2 }
                  }}
                >
                  Attached Files ({attachments.length})
                </Typography>

                <Stack spacing={{ xs: 1.5, sm: 1.5 }}>
                  {attachments.map(attachment => (
                    <Box
                      key={attachment.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        gap: { xs: 1.5, sm: 2 },
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'action.hover',
                          borderColor: 'action.selected'
                        }
                      }}
                    >
                      <InsertDriveFile
                        sx={{
                          color: 'primary.main',
                          fontSize: { xs: '1.25rem', sm: '1.5rem' }
                        }}
                      />

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: '0.875rem', sm: '0.925rem' },
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {attachment.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.8rem' },
                            display: 'block'
                          }}
                        >
                          {attachment.type} • {(attachment.size / 1024).toFixed(1)} KB
                        </Typography>
                      </Box>

                      <IconButton
                        size="small"
                        onClick={() => handleRemoveAttachment(attachment.id)}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': {
                            color: 'error.main',
                            bgcolor: 'error.light'
                          }
                        }}
                      >
                        <Cancel fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              </Box>
            ) : (
              <Alert
                severity="info"
                sx={{
                  mt: { xs: 1, sm: 1 },
                  '& .MuiAlert-message': {
                    fontSize: { xs: '0.875rem', sm: '0.925rem' }
                  }
                }}
              >
                No files or photos attached yet. Upload files or take photos to document your inspection findings.
              </Alert>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Inspection Checklist Accordion */}
        <Accordion
          expanded={checklistExpanded}
          onChange={handleChecklistAccordionChange}
          sx={{
            mb: { xs: 3, md: 4 },
            boxShadow: 1,
            border: '1px solid',
            borderColor: 'divider',
            '&:before': {
              display: 'none',
            },
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              bgcolor: 'grey.50',
              color: 'text.primary',
              borderBottom: checklistExpanded ? '1px solid' : 'none',
              borderColor: 'divider',
              '& .MuiAccordionSummary-content': {
                alignItems: 'center',
                gap: 2
              },
              '&:hover': {
                bgcolor: 'grey.100'
              },
              py: { xs: 1, sm: 1.5 }
            }}
          >
            <ChecklistRtl sx={{
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              color: 'primary.main'
            }} />
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  fontWeight: 600,
                  color: 'text.primary'
                }}
              >
                Inspection Checklist
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  color: 'text.secondary',
                  mt: 0.25
                }}
              >
                {checklist.filter(item => item.Checked).length} of {checklist.length} items completed
              </Typography>
            </Box>
            <Chip
              label={`${completionPercentage}%`}
              size="small"
              color={completionPercentage === 100 ? 'success' : 'primary'}
              variant="outlined"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '0.7rem', sm: '0.75rem' }
              }}
            />
          </AccordionSummary>

          <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 3, sm: 3 } }}>
            {/* Progress Bar */}
            <Box sx={{ mb: { xs: 3, sm: 3 } }}>
              <LinearProgress
                variant="determinate"
                value={completionPercentage}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                  }
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mt: 1,
                  fontSize: { xs: '0.75rem', sm: '0.8rem' }
                }}
              >
                Tap checklist items to mark as complete or incomplete
              </Typography>
            </Box>

            {/* Checklist Items */}
            {checklist.length > 0 ? (
              <List sx={{
                width: '100%',
                mt: { xs: 1, sm: 0 },
                '& .MuiListItem-root': {
                  px: 0,
                  py: { xs: 1.5, sm: 1.5 },
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': {
                    borderBottom: 'none'
                  }
                }
              }}>
                {checklist.map((item, index) => (
                  <ListItem key={index}>
                    <ListItemIcon sx={{ minWidth: { xs: 40, sm: 48 } }}>
                      <Checkbox
                        checked={item.Checked}
                        onChange={() => handleChecklistToggle(index)}
                        icon={<Cancel color="error" />}
                        checkedIcon={<CheckCircle color="success" />}
                        sx={{
                          '&.Mui-checked': {
                            color: 'success.main',
                          },
                          '&:not(.Mui-checked)': {
                            color: 'error.main',
                          },
                          transform: { xs: 'scale(1.1)', sm: 'scale(1.2)' }
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.Name}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontSize: { xs: '0.875rem', sm: '0.925rem' },
                          textDecoration: item.Checked ? 'line-through' : 'none',
                          color: item.Checked ? 'text.secondary' : 'text.primary',
                          fontWeight: item.Checked ? 400 : 500,
                          lineHeight: 1.4
                        }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Alert severity="info" sx={{ mt: 1 }}>
                No checklist items available for this inspection.
              </Alert>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Action Buttons */}
        <Box sx={{ mt: { xs: 4, md: 5 } }}>
          <Card sx={{ boxShadow: 1, border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontSize: { xs: '1.125rem', sm: '1.25rem' },
                  fontWeight: 600,
                  mb: 3,
                  color: 'text.primary'
                }}
              >
                Complete Inspection
              </Typography>

              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 2 },
                '& .MuiButton-root': {
                  py: { xs: 1.25, sm: 1 },
                  fontSize: { xs: '0.875rem', sm: '0.925rem' }
                }
              }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={handleMarkAsPass}
                  fullWidth={isMobile}
                  sx={{ fontWeight: 600 }}
                >
                  Mark as Pass
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Cancel />}
                  onClick={handleMarkAsFail}
                  fullWidth={isMobile}
                  sx={{ fontWeight: 600 }}
                >
                  Mark as Fail
                </Button>

                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<WarningAmber />}
                  onClick={handleMarkAsNotCompleted}
                  fullWidth={isMobile}
                  sx={{ fontWeight: 600 }}
                >
                  Mark as Not Completed
                </Button>

                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<Save />}
                  fullWidth={isMobile}
                  sx={{
                    fontWeight: 500,
                    borderColor: 'divider',
                    color: 'text.primary',
                    '&:hover': {
                      borderColor: 'action.selected',
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  Save Progress
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>

      {/* Back Confirmation Dialog */}
      <Dialog
        open={showBackConfirmation}
        onClose={handleCancelBack}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: isMobile ? 0 : 2,
            m: isMobile ? 0 : 2
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 3 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <WarningAmber
              color="warning"
              sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}
            />
            <Typography
              variant={isMobile ? "h6" : "h5"}
              component="h2"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '1.125rem', sm: '1.25rem' }
              }}
            >
              Confirm Navigation
            </Typography>
          </Box>

          <IconButton
            onClick={handleCancelBack}
            size={isMobile ? "medium" : "large"}
            sx={{
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover' }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 3 }
        }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <WarningAmber
              sx={{
                fontSize: { xs: '3rem', sm: '4rem' },
                color: 'warning.main',
                mb: 2
              }}
            />

            <Typography
              variant={isMobile ? "h6" : "h5"}
              gutterBottom
              sx={{
                fontWeight: 600,
                mb: 1,
                fontSize: { xs: '1.125rem', sm: '1.25rem' }
              }}
            >
              Are you sure you want to go back?
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                fontSize: { xs: '0.875rem', sm: '1rem' },
                lineHeight: 1.5
              }}
            >
              You will leave the inspection in progress. Your current progress will be saved, but you'll need to return to continue the inspection.
            </Typography>
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          gap: { xs: 1, sm: 1.5 },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          '& > :not(style) ~ :not(style)': {
            marginLeft: 0
          }
        }}>
          <Button
            onClick={handleCancelBack}
            variant="outlined"
            color="inherit"
            fullWidth={isMobile}
            sx={{
              fontSize: { xs: '0.875rem', sm: '0.925rem' },
              py: { xs: 1.25, sm: 1 },
              px: { xs: 3, sm: 4 },
              fontWeight: 500
            }}
          >
            Stay Here
          </Button>

          <Button
            onClick={handleConfirmBack}
            variant="contained"
            color="warning"
            fullWidth={isMobile}
            startIcon={<ArrowBack />}
            sx={{
              fontSize: { xs: '0.875rem', sm: '0.925rem' },
              py: { xs: 1.25, sm: 1 },
              px: { xs: 3, sm: 4 },
              fontWeight: 600,
              minHeight: { xs: 48, sm: 40 }
            }}
          >
            Yes, Go Back
          </Button>
        </DialogActions>
      </Dialog>

      {/* Camera Capture Dialog */}
      <SimpleCamera
        open={cameraOpen}
        onClose={handleCloseCamera}
        onCapture={handleCapturedPhoto}
      />

      {/* Completion Dialog */}
      <CompletionDialog
        open={completionDialogOpen}
        onClose={handleCloseCompletionDialog}
        inspection={inspection}
        onComplete={handleCompleteInspection}
        loading={loading}
        reportIssue={completionType === 'fail'}
        inspectionNotCompleted={completionType === 'not-completed'}
      />
    </>
  );
};

export default InProgress;